import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, Bot, Building2, DollarSign, Power, PowerOff,
  RefreshCw, Clock, Users, CheckCircle, XCircle, Shield, ClipboardCheck,
  Zap, BarChart3, Eye, ArrowRight, AlertCircle, Pause, Play, Wallet, HeartPulse,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import StuckJobsWidget from '../components/StuckJobsWidget';
import ApolloSpendWidget from '../components/ApolloSpendWidget';
import PastDueAlertsWidget from '../components/PastDueAlertsWidget';
import {
  PageHeader, MetricRow, EmptyState, StatusDot, ConfirmDestructive,
} from '../design';

/**
 * Governor Dashboard — Iter 62.7 redesign.
 *
 * Structure (top → bottom):
 *   1. PageHeader: title + subtitle + time range + refresh
 *   2. Hero MetricRow: 4 KPIs (Health, Approvals, Cost, Executions)
 *   3. Stats row: 5 secondary counts (Orgs/Tenants/Users/Success/Errors)
 *   4. Operations cluster: StuckJobs + ApolloSpend side-by-side
 *   5. Anomalies / Kill switches alerts (conditional)
 *   6. Global Control card
 *   7. Activity trend + Top agents (2 col)
 *   8. Active kill switches + Quick actions
 */

const TIME_RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
];

export default function Dashboard() {
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [dashboardData, setDashboardData] = useState(null);
  const [killSwitches, setKillSwitches] = useState([]);
  const [globalKillActive, setGlobalKillActive] = useState(false);
  const [billingHealthCount, setBillingHealthCount] = useState(0);
  const inFlightRef = useRef(false);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (inFlightRef.current) return;          // prevent overlapping polls
    inFlightRef.current = true;
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    try {
      const [metricsRes, killRes] = await Promise.allSettled([
        governanceAPI.getDashboardMetrics(timeRange),
        governanceAPI.getKillSwitches(),
      ]);
      governanceAPI.getBillingIntegrityCount()
        .then((r) => setBillingHealthCount(r?.data?.count || 0))
        .catch(() => {});
      if (metricsRes.status === 'fulfilled') setDashboardData(metricsRes.value.data);
      if (killRes.status === 'fulfilled') {
        const data = killRes.value.data;
        const switches = data.kill_switch_state || {};
        const flat = [];
        if (switches.global?.active) {
          flat.push({ scope: 'global', target_id: 'global', ...switches.global });
          setGlobalKillActive(true);
        } else {
          setGlobalKillActive(false);
        }
        Object.entries(switches.tenants || {}).forEach(([id, s]) => s.active && flat.push({ scope: 'tenant', target_id: id, ...s }));
        Object.entries(switches.agents || {}).forEach(([id, s]) => s.active && flat.push({ scope: 'agent', target_id: id, ...s }));
        Object.entries(switches.channels || {}).forEach(([id, s]) => s.active && flat.push({ scope: 'channel', target_id: id, ...s }));
        setKillSwitches(flat);
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('[Dashboard] Failed to fetch:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
      inFlightRef.current = false;
    }
  }, [timeRange]);

  // Initial load + 30s auto-refresh. The fetch is routed through a ref (assigned
  // in an effect, never during render) so the poll effect has a single stable
  // dependency and doesn't re-subscribe on every loading/refreshing toggle.
  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; });
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    fetchDataRef.current();
    const interval = setInterval(() => fetchDataRef.current(true), 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, timeRange]);

  // Live billing-health badge — push over the GOVERNANCE websocket channel so the
  // count updates the instant corruption is flagged/resolved (no manual refresh).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const token = localStorage.getItem('superadmin_token');
    const base = process.env.REACT_APP_BACKEND_URL || '';
    if (!token || !/^https?:\/\//.test(base) || base.includes('localhost')) return undefined;
    const wsBase = base.replace(/^http/, 'ws');
    let ws;
    try {
      ws = new WebSocket(`${wsBase}/api/ws/connect?token=${token}&channels=governance`);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'billing_health_changed' && typeof msg.data?.count === 'number') {
            setBillingHealthCount(msg.data.count);
          }
        } catch { /* noop */ }
      };
    } catch { /* noop */ }
    return () => { try { ws && ws.close(1000); } catch { /* noop */ } };
  }, [isAuthenticated]);

  const toggleKillSwitch = async (on, reason) => {
    try {
      await governanceAPI.executeSuperAdminAction({
        action: on ? 'KILL_SWITCH_ON' : 'KILL_SWITCH_OFF',
        target_scope: 'global',
        config: { reason: reason || 'SuperAdmin action' },
      });
      toast.success(`Global AI ${on ? 'stopped' : 'enabled'}`);
      setGlobalKillActive(on);
      fetchData(true);
    } catch (_e) {
      toast.error('Error updating kill switch');
    }
  };

  const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;
  const fmtNum = (n) => {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return v.toLocaleString();
  };

  const health = dashboardData?.health || { score: 100, status: 'good' };
  const metrics = dashboardData?.metrics || {};
  const counts = dashboardData?.counts || {};
  const activityTrend = dashboardData?.activity_trend || [];
  const topAgents = dashboardData?.top_agents || [];
  const anomalies = dashboardData?.anomalies || [];

  const healthTone =
    health.score >= 90 ? 'success' :
    health.score >= 70 ? 'accent' :
    health.score >= 50 ? 'warning' : 'danger';

  const heroMetrics = [
    {
      key: 'health',
      label: 'Platform Health',
      value: `${health.score}/100`,
      hint: health.status?.charAt(0).toUpperCase() + health.status?.slice(1) || 'Operational',
      tone: healthTone,
      icon: Shield,
    },
    {
      key: 'approvals',
      label: 'Pending Approvals',
      value: metrics.pending_approvals || 0,
      hint: metrics.pending_approvals > 0 ? 'Awaiting governor decision' : 'Queue is clear',
      tone: metrics.pending_approvals > 0 ? 'warning' : 'muted',
      icon: ClipboardCheck,
    },
    {
      key: 'cost',
      label: 'Cost (MTD)',
      value: fmtUSD(metrics.cost_mtd?.total),
      hint: metrics.cost_mtd?.previous_month != null ? `Last month ${fmtUSD(metrics.cost_mtd?.previous_month)}` : null,
      tone: 'default',
      icon: DollarSign,
    },
    {
      key: 'executions',
      label: 'AI Executions',
      value: fmtNum(metrics.executions?.total),
      delta: metrics.executions?.change_percent ? `${metrics.executions.change_percent}%` : undefined,
      deltaLabel: 'vs prev period',
      hint: `${TIME_RANGE_OPTIONS.find(o => o.value === timeRange)?.label.toLowerCase()}`,
      tone: 'default',
      icon: Zap,
    },
  ];

  const secondaryMetrics = [
    { key: 'orgs',     label: 'Organizations', value: counts.organizations || 0, hint: counts.organizations_total ? `${counts.organizations_total} total` : null, icon: Building2 },
    { key: 'tenants',  label: 'Tenants',       value: counts.tenants || 0,          icon: Users },
    { key: 'users',    label: 'Users',         value: counts.users || 0,            icon: Users },
    { key: 'success',  label: 'Success Rate',  value: metrics.success_rate == null ? '—' : `${metrics.success_rate}%`, hint: metrics.success_rate == null ? 'No activity' : null, icon: CheckCircle, tone: metrics.success_rate == null ? 'muted' : 'success' },
    { key: 'errors',   label: 'Errors',        value: metrics.error_count || 0,     icon: XCircle, tone: metrics.error_count > 0 ? 'danger' : 'muted' },
  ];

  const maxTrend = Math.max(...activityTrend.map(d => d.executions), 1);
  const avgTrend = activityTrend.length > 0
    ? Math.round(activityTrend.reduce((s, d) => s + d.executions, 0) / activityTrend.length)
    : 0;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* 1. Page header */}
      <PageHeader
        eyebrow="Overview"
        title="AI Governance Dashboard"
        subtitle="Monitor and control AI agent operations across all tenants"
        actions={
          <>
            {billingHealthCount > 0 && (
              <Link
                to="/governance/billing-drift"
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 text-sm font-medium text-rose-500 transition-colors hover:bg-rose-500/20"
                data-testid="billing-health-badge"
                title="Accounts in a corrupt/half-upgraded billing state"
              >
                <HeartPulse className="h-3.5 w-3.5" />
                Billing Health: {billingHealthCount}
              </Link>
            )}
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="h-9 w-44 border-[var(--gov-border)] bg-[var(--gov-bg-input)] text-sm" data-testid="time-range-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_RANGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="h-9 border-[var(--gov-border)] bg-[var(--gov-bg-input)]"
              data-testid="refresh-button"
            >
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {lastUpdated && (
              <span className="gov-text-tertiary gov-tabular hidden items-center gap-1 text-xs sm:inline-flex">
                <Clock className="h-3 w-3" /> {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </>
        }
      />

      {/* Active kill-switch banner (was buried as 11px grey text) */}
      {killSwitches.length > 0 && (
        <div className="gov-card flex items-center justify-between gap-4 border-rose-500/40 bg-rose-500/5 p-3.5" data-testid="active-killswitch-banner">
          <div className="flex items-center gap-3">
            <PowerOff className="h-4 w-4 shrink-0 text-rose-400" />
            <div>
              <p className="text-sm font-medium text-rose-300">
                {killSwitches.length} active kill switch{killSwitches.length === 1 ? '' : 'es'} — AI halted for {[...new Set(killSwitches.map(k => k.scope))].join(', ')}
              </p>
              <p className="gov-text-secondary text-xs">
                {globalKillActive ? 'Platform-wide AI is STOPPED for all tenants.' : 'Scoped kill switches are active.'} Affected work stays paused until re-enabled.
              </p>
            </div>
          </div>
          <Link to={globalKillActive ? '/governance/dashboard' : '/governance/agents'}>
            <Button variant="outline" size="sm" className="h-7 text-xs"><Eye className="mr-1.5 h-3 w-3" /> Review</Button>
          </Link>
        </div>
      )}

      {/* 2. Hero metrics */}
      <MetricRow metrics={heroMetrics} cols={4} />

      {/* 3. Secondary stats */}
      <MetricRow metrics={secondaryMetrics} cols={5} />

      {/* 4. Operations cluster */}
      <div className="grid gap-4 lg:grid-cols-2" data-testid="ops-cluster">
        <StuckJobsWidget minutes={5} />
        <ApolloSpendWidget days={7} />
      </div>

      {/* 4b. Churn radar — proactive past-due alerts */}
      <PastDueAlertsWidget />

      {/* 5. Anomalies (conditional) */}
      {anomalies.length > 0 && (
        <div className="space-y-2" data-testid="anomaly-alerts">
          {anomalies.map((anomaly, idx) => {
            const isCritical = anomaly.severity === 'critical';
            return (
              <div
                key={idx}
                className={`gov-card flex items-center justify-between gap-4 p-3.5 ${
                  isCritical ? 'border-rose-500/40 bg-rose-500/5' : 'border-amber-500/40 bg-amber-500/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className={`h-4 w-4 shrink-0 ${isCritical ? 'text-rose-400' : 'text-amber-400'}`} />
                  <div>
                    <p className={`text-sm font-medium ${isCritical ? 'text-rose-300' : 'text-amber-300'}`}>
                      {anomaly.type === 'execution_spike' ? 'Unusual activity spike' :
                       anomaly.type === 'execution_drop' ? 'Activity drop detected' :
                       anomaly.type === 'high_error_rate' ? 'High error rate' : 'Anomaly detected'}
                    </p>
                    <p className="gov-text-secondary text-xs">{anomaly.message}</p>
                  </div>
                </div>
                <Link to="/governance/audit">
                  <Button variant="outline" size="sm" className="h-7 text-xs">
                    <Eye className="mr-1.5 h-3 w-3" /> Investigate
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* 6. Global control */}
      <div className="gov-card p-5" data-testid="global-control-card">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="gov-text-primary text-sm font-semibold">Global AI Control</h3>
            <p className="gov-text-tertiary text-xs">Emergency switch that stops or resumes ALL AI workers platform-wide.</p>
          </div>
          <StatusDot
            status={globalKillActive ? 'paused' : 'enabled'}
            label={globalKillActive ? 'AI Stopped' : 'AI Enabled'}
            variant="pill"
            pulse={globalKillActive}
            testid="global-status"
          />
        </div>
        <div className="flex items-center gap-2">
          <ConfirmDestructive
            trigger={
              <Button variant="destructive" size="sm" disabled={globalKillActive || loading} data-testid="stop-all-ai">
                <PowerOff className="mr-1.5 h-3.5 w-3.5" /> Stop all AI
              </Button>
            }
            title="Stop ALL AI workers platform-wide?"
            description="This immediately halts every cadence email, AI call, scoring run, and reveal across every tenant. Customer impact will be visible within seconds."
            confirmLabel="Stop all AI"
            onConfirm={(reason) => toggleKillSwitch(true, reason)}
            testId="confirm-stop-all"
          />
          <Button
            variant="default"
            size="sm"
            onClick={() => toggleKillSwitch(false, 'Re-enabled from dashboard')}
            disabled={!globalKillActive}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="enable-ai"
          >
            <Power className="mr-1.5 h-3.5 w-3.5" /> Enable AI
          </Button>
          <span className="gov-text-tertiary ml-auto text-xs">
            {killSwitches.length} active kill switch{killSwitches.length === 1 ? '' : 'es'}
          </span>
        </div>
      </div>

      {/* 7. Activity trend + top agents */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="gov-card p-5" data-testid="activity-trend-card">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="gov-text-primary flex items-center gap-2 text-sm font-semibold">
                <BarChart3 className="h-4 w-4 text-[var(--gov-accent)]" /> AI Activity Trend
              </h3>
              <p className="gov-text-tertiary text-xs">Executions per day • Average: <span className="gov-text-primary font-medium">{avgTrend}</span></p>
            </div>
          </div>
          {activityTrend.length > 0 ? (
            <div className="flex h-44 items-end gap-2 px-1">
              {activityTrend.map((day, idx) => {
                const heightPercent = maxTrend > 0 ? (day.executions / maxTrend) * 100 : 0;
                const isAboveAvg = day.executions > avgTrend;
                return (
                  <div key={idx} className="group flex flex-1 flex-col items-center justify-end gap-1">
                    <span className="gov-text-primary gov-tabular text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                      {day.executions}
                    </span>
                    <div
                      className={`w-full max-w-10 rounded-t-md transition-all ${
                        isAboveAvg ? 'bg-emerald-500/70 group-hover:bg-emerald-500' : 'bg-indigo-500/60 group-hover:bg-indigo-500'
                      }`}
                      style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: day.executions > 0 ? '20px' : '4px' }}
                      title={`${day.label}: ${day.executions} executions`}
                    />
                    <span className="gov-text-tertiary text-[10px]">{day.label?.split(' ')[1] || day.date?.slice(-2)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={BarChart3} title="No activity yet" hint="Try a longer time range" size="sm" />
          )}
        </div>

        <div className="gov-card p-5" data-testid="top-agents-card">
          <div className="mb-4">
            <h3 className="gov-text-primary flex items-center gap-2 text-sm font-semibold">
              <Bot className="h-4 w-4 text-[var(--gov-accent)]" /> Top Agents
            </h3>
            <p className="gov-text-tertiary text-xs">By execution count</p>
          </div>
          {topAgents.length > 0 ? (
            <div className="space-y-3">
              {topAgents.map((agent, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="gov-text-primary font-medium">{idx + 1}. {agent.name}</span>
                    <span className="gov-text-tertiary gov-tabular">{agent.executions} • {agent.percentage}%</span>
                  </div>
                  <Progress value={agent.percentage} className="h-1.5" />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Bot} title="No agent activity" size="sm" />
          )}
        </div>
      </div>

      {/* 8. Active kill switches */}
      <div className="gov-card p-5" data-testid="kill-switches-card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="gov-text-primary flex items-center gap-2 text-sm font-semibold">
            <Pause className="h-4 w-4 text-rose-400" /> Active Kill Switches
          </h3>
          <span className="gov-text-tertiary text-xs">{killSwitches.length}</span>
        </div>
        {killSwitches.length === 0 ? (
          <EmptyState icon={Play} title="All systems operational" hint="No kill switches are active right now." size="sm" />
        ) : (
          <div className="space-y-2">
            {killSwitches.map((ks, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-[var(--gov-border)] bg-[var(--gov-bg-elevated)] px-3 py-2">
                <div className="flex items-center gap-3">
                  <StatusDot status="paused" pulse size="xs" label={null} />
                  <div>
                    <p className="gov-text-primary text-sm font-medium">{ks.target_id}</p>
                    <p className="gov-text-tertiary text-xs">{ks.scope} • {ks.reason || 'no reason recorded'}</p>
                  </div>
                </div>
                <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-[11px] capitalize text-rose-400">
                  {ks.scope}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 9. Quick actions */}
      <div className="gov-card p-5" data-testid="quick-actions-card">
        <h3 className="gov-text-primary mb-3 text-sm font-semibold">Quick Actions</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { to: '/governance/approvals', icon: ClipboardCheck, label: 'Approvals', count: metrics.pending_approvals },
            { to: '/governance/organizations', icon: Building2, label: 'Organizations' },
            { to: '/governance/tenants', icon: Users, label: 'Tenants' },
            { to: '/governance/budgets', icon: Wallet, label: 'Budgets' },
            { to: '/governance/audit', icon: Activity, label: 'Audit' },
          ].map(({ to, icon: Icon, label, count }) => (
            <Link key={to} to={to}>
              <Button
                variant="outline"
                className="w-full justify-start border-[var(--gov-border)] bg-[var(--gov-bg-input)] text-sm font-normal hover:border-[var(--gov-border-strong)] hover:bg-[var(--gov-bg-elevated)]"
                data-testid={`quick-action-${label.toLowerCase()}`}
              >
                <Icon className="mr-2 h-3.5 w-3.5" />
                {label}
                {count > 0 && <Badge variant="destructive" className="ml-auto h-4 px-1 text-[10px]">{count}</Badge>}
              </Button>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
